import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';
import { type EntityType } from '~/types/entity';

import { promiseAllThrottled } from '~/utils/promise';

import { addToUserGroup } from './useMutationAddToUserGroup';
import { deleteFromUserGroup } from './useMutationDeleteFromUserGroup';

/**
 * Type of user group update operation.
 * - updateParentUserGroups: Add the current user group to / remove it from a list of user groups.
 * - updateUserGroupEntities: Add a list of entities to / remove it from the current user group.
 */
type UpdateType = 'updateParentUserGroups' | 'updateUserGroupEntities';

type UpdateUserGroupsParams = {
  addedMembers: UUID[];
  deletedMembers: UUID[];
  entityId: UUID;
  entityType: Extract<EntityType, 'user' | 'user_group'>;
  updateType: UpdateType;
};

type UpdateUserGroupsMutationOptions = UseMutationOptions<
  void,
  Error,
  UpdateUserGroupsParams
>;

export type UpdateUserGroupsMutateAsyncFunction = UseMutateAsyncFunction<
  void,
  Error,
  UpdateUserGroupsParams
>;

/**
 * Updates the user group memberships for an entity by making API calls to add and remove members.
 *
 * @param {UpdateUserGroupsParams} params - Parameters for the update operation
 * @throws {Error} If the API calls fail
 * @returns {Promise<void>}
 */
const updateUserGroups = async ({
  addedMembers,
  deletedMembers,
  entityId,
  entityType,
  updateType = 'updateUserGroupEntities',
}: UpdateUserGroupsParams) => {
  let promisesAdd: Array<Promise<void>> = [];
  let promisesDelete: Array<Promise<void>> = [];

  if (updateType === 'updateParentUserGroups') {
    promisesAdd = addedMembers.map(async (parentUserGroupId) =>
      addToUserGroup(parentUserGroupId, entityType, entityId),
    );
    promisesDelete = deletedMembers.map(async (parentUserGroupId) =>
      deleteFromUserGroup(parentUserGroupId, entityType, entityId),
    );
  } else {
    promisesAdd = addedMembers.map(async (memberId) =>
      addToUserGroup(entityId, entityType, memberId),
    );
    promisesDelete = deletedMembers.map(async (memberId) =>
      deleteFromUserGroup(entityId, entityType, memberId),
    );
  }

  await promiseAllThrottled([...promisesAdd, ...promisesDelete]);
};

/**
 * Custom hook for managing user group updates using react-query mutations.
 *
 * @param {Parameters<typeof useMutation>[0]} [options] - Optional react-query mutation options
 * @returns {UseMutationResult} Mutation object for handling the update operation
 *
 * @example
 * ```tsx
 * const mutation = useMutationUpdateUserGroups();
 *
 * const handleUpdate = () => {
 *   mutation.mutate({
 *     addedMembers: ['user1', 'user2'],
 *     deletedMembers: ['user3'],
 *     entityId: '123',
 *     entityType: 'user_group',
 *     updateType: 'updateUserGroupEntities',
 *   });
 * };
 * ```
 */
export const useMutationUpdateUserGroups = (
  options?: UpdateUserGroupsMutationOptions,
) =>
  useMutation({
    mutationFn: updateUserGroups,
    ...options,
  });
